import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ChartOptionsMenu from '../../../placeholders/ChartOptionsMenu';
import ChartSeriesLegend from '../../../placeholders/ChartSeriesLegend';
import {
  discoverDimensionOptions,
  filterItemsByDimensions,
  formatChartMetricTooltip,
  formatChartMetricValue,
  isFilterVisible,
  resolveDimensionValue,
  toggleVisibilityKey,
  visibilityMapFromOptions,
} from '../../../placeholders/chartFilterUtils';
import { isDateInSelectedPeriod, parseDateSafe } from '../../../placeholders/periodSummaryUtils';
import { useUIColors } from '../../../../hooks/useTheme';
import {
  getExpenseReports,
  getJournalEntries,
  getLedger,
  getRegisterDocuments,
  getTreasuryEntries,
} from '../../../../services/treasuryAPI';

const CHART_PLOT_HEIGHT_PX = 234;

const DEBE_COLOR = '#16a34a';
const HABER_COLOR = '#ea580c';
const SALDO_COLOR = '#2563eb';
const TOTAL_COLOR = '#0066cc';
const CARGOS_LABEL = 'Cargos';
const CREDITOS_LABEL = 'Notas de crédito';
const INGRESOS_LABEL = 'Ingresos';
const EGRESOS_LABEL = 'Egresos';

const FALLBACK_PALETTE = [
  '#0066cc',
  '#00a651',
  '#f39c12',
  '#9b59b6',
  '#e74c3c',
  '#3498db',
  '#1abc9c',
  '#e67e22',
  '#34495e',
  '#7f8c8d',
];

const REGISTER_SECTIONS = new Set([
  'ventas',
  'compras',
  'remuneraciones',
  'honorarios',
  'invoice',
  'rendiciones',
]);

const SECTION_TITLES = {
  ventas: 'Distribución · Libro de ventas',
  compras: 'Distribución · Libro de compras',
  remuneraciones: 'Distribución · Remuneraciones',
  honorarios: 'Distribución · Honorarios',
  invoice: 'Distribución · Invoice',
  rendiciones: 'Distribución · Rendiciones',
  diario: 'Distribución · Libro diario',
  asientos: 'Distribución · Asientos',
  mayor: 'Distribución · Libro mayor',
  treasury: 'Movimientos bancarios',
};

const AXIS_MODES = [{ key: 'month', label: 'Mes' }];

const SIDE_DIMENSION = {
  key: 'side',
  label: 'Debe / Haber',
  order: ['DEBE', 'HABER'],
  colors: { DEBE: DEBE_COLOR, HABER: HABER_COLOR },
  fallback: 'DEBE',
};

const DOC_TYPE_DIMENSION = {
  key: 'docType',
  label: 'Tipo doc.',
  fallback: 'Sin tipo',
};

const STATUS_DIMENSION = {
  key: 'status',
  label: 'Estado',
  fallback: 'Sin estado',
};

const VENTAS_STATUS_DIMENSION = {
  key: 'status',
  label: 'Estado',
  order: ['Pagada', 'Por vencer', 'Vencida', 'Anulada', 'No aplica'],
  colors: {
    Pagada: '#16a34a',
    'Por vencer': '#d97706',
    Vencida: '#dc2626',
    Anulada: '#7c3aed',
    'No aplica': '#94a3b8',
  },
  fallback: 'Por vencer',
};

const ACCOUNT_DIMENSION = {
  key: 'account',
  label: 'Cuenta',
  fallback: 'Sin cuenta',
};

const TOTAL_DIMENSION = {
  key: 'total',
  label: 'Total',
  order: ['Total'],
  colors: { Total: TOTAL_COLOR },
  fallback: 'Total',
};

const FLOW_DIMENSION = {
  key: 'flowGroup',
  label: 'Flujo',
  order: [CARGOS_LABEL, CREDITOS_LABEL],
  colors: { [CARGOS_LABEL]: DEBE_COLOR, [CREDITOS_LABEL]: HABER_COLOR },
  fallback: CARGOS_LABEL,
};

const TREASURY_FLOW_DIMENSION = {
  key: 'flowGroup',
  label: 'Ingresos / Egresos',
  order: [INGRESOS_LABEL, EGRESOS_LABEL],
  colors: { [INGRESOS_LABEL]: DEBE_COLOR, [EGRESOS_LABEL]: HABER_COLOR },
  fallback: INGRESOS_LABEL,
};

const TREASURY_STATUS_DIMENSION = {
  key: 'status',
  label: 'Conciliación',
  order: ['Pendiente', 'Conciliado'],
  fallback: 'Pendiente',
};

const CATEGORY_DIMENSION = {
  key: 'category',
  label: 'Categoría',
  fallback: 'Sin categoría',
};

const TREASURY_DIMENSIONS = [
  TREASURY_FLOW_DIMENSION,
  ACCOUNT_DIMENSION,
  TREASURY_STATUS_DIMENSION,
  CATEGORY_DIMENSION,
];

const TREASURY_METRICS = [
  { key: 'detalle', label: 'Ingresos / Egresos', totalLabel: 'CLP' },
  { key: 'saldo', label: 'Flujo neto', totalLabel: 'CLP' },
  { key: 'saldo_acumulado', label: 'Flujo neto acumulado', totalLabel: 'CLP' },
  { key: 'amount', label: 'Monto', totalLabel: 'CLP' },
  { key: 'count', label: 'Cantidad', totalLabel: 'movimientos' },
];

const REGISTER_METRICS = [
  { key: 'amount', label: 'Monto', totalLabel: 'CLP' },
  { key: 'count', label: 'Cantidad', totalLabel: 'registros' },
];

const VENTAS_METRICS = [
  { key: 'count', label: 'Cantidad de facturas', totalLabel: 'facturas' },
  { key: 'amount', label: 'Montos c/IVA', totalLabel: 'CLP' },
];

const MAYOR_METRICS = [
  { key: 'breakdown', label: 'DEBE / HABER', totalLabel: 'CLP' },
  { key: 'debe', label: 'DEBE', totalLabel: 'CLP' },
  { key: 'haber', label: 'HABER', totalLabel: 'CLP' },
  { key: 'saldo', label: 'Saldo del mes', totalLabel: 'CLP' },
  { key: 'saldo_acumulado', label: 'Saldo acumulado', totalLabel: 'CLP' },
];

const JOURNAL_METRICS = [
  { key: 'amount', label: 'Monto DEBE', totalLabel: 'CLP' },
  { key: 'count', label: 'Cantidad', totalLabel: 'asientos' },
];

const normalizeDocTypeKey = (value = '') =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');

/** Notas de crédito y documentos equivalentes (reversas). */
const isCreditNoteDoc = (docType = '') => {
  const key = normalizeDocTypeKey(docType);
  return key.includes('credito');
};

const accountLabelOf = (row) => {
  const code = String(row.account_code_snapshot || row.account_code || '').trim();
  const label = String(row.account_label_snapshot || row.account_label || '').trim();
  if (code && label) return `${code} · ${label}`;
  return code || label || 'Sin cuenta';
};

const accountKeyOf = (row) => {
  if (row.chart_account_id != null && row.chart_account_id !== '') {
    return `id:${row.chart_account_id}`;
  }
  const code = String(row.account_code_snapshot || row.account_code || '').trim();
  const label = String(row.account_label_snapshot || row.account_label || '').trim();
  if (code || label) return `${code}|${label}`;
  return 'Sin cuenta';
};

const accountIdOf = (row) => {
  const id = Number(row.chart_account_id);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const buildColorMap = (keys, explicitColors, palette) => {
  const map = { ...(explicitColors || {}) };
  let paletteIndex = 0;
  keys.forEach((key) => {
    if (!map[key]) {
      map[key] = palette[paletteIndex % palette.length];
      paletteIndex += 1;
    }
  });
  return map;
};

const moneyMetricKey = (metricKey) => (metricKey === 'count' ? 'count' : 'amount');

/**
 * Gráfico contable con el mismo patrón visual/controles que Cotizaciones.
 */
const AccountingMonthlyChart = ({
  section = 'ventas',
  refreshKey = 0,
  periods = [],
  periodRange = null,
  selectedPeriod = null,
  onDateValuesChange,
  onFocusedAccountIdsChange,
  documents = null,
  documentsLoading = false,
  documentsError = '',
  fillWidth = false,
  defaultMetricKey = '',
  defaultStackKey = '',
}) => {
  const ui = useUIColors();
  const theme = ui.theme || {};
  const chartColors = theme.chart || {};
  const scrollRef = useRef(null);
  const palette = useMemo(
    () =>
      [
        chartColors.primary,
        chartColors.tertiary,
        chartColors.quaternary,
        chartColors.quinary,
        chartColors.secondary,
        chartColors.senary,
        ...FALLBACK_PALETTE,
      ].filter(Boolean),
    [chartColors]
  );

  const isMayor = section === 'mayor';
  const isVentas = section === 'ventas';
  const isRegister = REGISTER_SECTIONS.has(section);
  const isJournal = section === 'asientos' || section === 'diario';
  const isTreasury = section === 'treasury';
  const usesSharedDocuments = (isRegister && section !== 'rendiciones') || isTreasury;
  const [positiveFlowLabel, negativeFlowLabel] = isTreasury
    ? [INGRESOS_LABEL, EGRESOS_LABEL]
    : [CARGOS_LABEL, CREDITOS_LABEL];

  const stackDimensions = useMemo(() => {
    if (isMayor) return [SIDE_DIMENSION, ACCOUNT_DIMENSION];
    if (isVentas) return [VENTAS_STATUS_DIMENSION, DOC_TYPE_DIMENSION, FLOW_DIMENSION];
    if (isRegister) return [DOC_TYPE_DIMENSION, STATUS_DIMENSION];
    if (isJournal) return [STATUS_DIMENSION];
    if (isTreasury) return TREASURY_DIMENSIONS;
    return [TOTAL_DIMENSION];
  }, [isMayor, isVentas, isRegister, isJournal, isTreasury]);

  const valueMetrics = useMemo(() => {
    if (isMayor) return MAYOR_METRICS;
    if (isVentas) return VENTAS_METRICS;
    if (isJournal) return JOURNAL_METRICS;
    if (isTreasury) return TREASURY_METRICS;
    return REGISTER_METRICS;
  }, [isMayor, isVentas, isJournal, isTreasury]);

  const filterOnlyDimensions = useMemo(() => {
    if (isMayor) return [ACCOUNT_DIMENSION];
    if (isVentas) return [VENTAS_STATUS_DIMENSION, DOC_TYPE_DIMENSION, FLOW_DIMENSION];
    if (isRegister) return [DOC_TYPE_DIMENSION, STATUS_DIMENSION];
    if (isJournal) return [STATUS_DIMENSION];
    if (isTreasury) return TREASURY_DIMENSIONS;
    return [];
  }, [isMayor, isVentas, isRegister, isJournal, isTreasury]);

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [stackKey, setStackKey] = useState(
    defaultStackKey && stackDimensions.some((dimension) => dimension.key === defaultStackKey)
      ? defaultStackKey
      : stackDimensions[0]?.key || 'total'
  );
  const [metricKey, setMetricKey] = useState(
    defaultMetricKey && valueMetrics.some((metric) => metric.key === defaultMetricKey)
      ? defaultMetricKey
      : valueMetrics[0]?.key || 'amount'
  );
  const [axisKey] = useState('month');
  const [dimensionVisibility, setDimensionVisibility] = useState({});
  const [monthVisibility, setMonthVisibility] = useState({});

  useEffect(() => {
    if (!stackDimensions.some((d) => d.key === stackKey)) {
      setStackKey(stackDimensions[0]?.key || 'total');
    }
  }, [stackDimensions, stackKey]);

  useEffect(() => {
    if (!valueMetrics.some((m) => m.key === metricKey)) {
      const fallback =
        defaultMetricKey && valueMetrics.some((m) => m.key === defaultMetricKey)
          ? defaultMetricKey
          : valueMetrics[0]?.key || 'amount';
      setMetricKey(fallback);
    }
  }, [valueMetrics, metricKey, defaultMetricKey]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      let nextItems = [];

      if (isMayor) {
        const rows = await getLedger();
        nextItems = (Array.isArray(rows) ? rows : []).map((row, index) => {
          const side = String(row.side || '').toLowerCase() === 'haber' ? 'HABER' : 'DEBE';
          const amount = Math.abs(Number(row.amount || 0));
          return {
            id: row.id || `ledger-${index}`,
            date: row.entry_date,
            side,
            account: accountLabelOf(row),
            accountKey: accountKeyOf(row),
            chartAccountId: accountIdOf(row),
            amount,
            signedAmount: side === 'DEBE' ? amount : -amount,
            status: 'Movimiento',
            docType: 'Movimiento',
            flowGroup: CARGOS_LABEL,
          };
        });
      } else if (isRegister) {
        let rows;
        if (section === 'rendiciones') {
          rows = await getExpenseReports();
        } else if (documents !== null) {
          rows = documents;
        } else {
          rows = await getRegisterDocuments({ register_type: section });
        }
        nextItems = (Array.isArray(rows) ? rows : []).map((row, index) => {
          const docType =
            section === 'rendiciones'
              ? String(row.report_type || '').trim() || 'Sin tipo'
              : String(row.document_type || '').trim() || 'Sin tipo';
          const amount = Math.abs(Number(row.total_amount || 0));
          const credit = isVentas && isCreditNoteDoc(docType);
          const flowGroup = credit ? CREDITOS_LABEL : CARGOS_LABEL;
          return {
            id: row.id || `reg-${index}`,
            date: section === 'rendiciones' ? row.report_date : row.doc_date,
            amount,
            signedAmount: isVentas ? (credit ? -amount : amount) : amount,
            docType,
            status: String(row.collection_status || row.status || '').trim() || 'Sin estado',
            side: 'Total',
            account: 'Total',
            flowGroup,
          };
        });
      } else if (isJournal) {
        const params = section === 'diario' ? { status: 'Contabilizado' } : {};
        const rows = await getJournalEntries(params);
        nextItems = (Array.isArray(rows) ? rows : []).map((row, index) => ({
          id: row.id || `je-${index}`,
          date: row.entry_date,
          amount: Math.abs(Number(row.total_debe || 0)),
          signedAmount: Math.abs(Number(row.total_debe || 0)),
          status: String(row.status || '').trim() || 'Sin estado',
          docType: String(row.status || '').trim() || 'Sin estado',
          side: 'Total',
          account: 'Total',
          flowGroup: CARGOS_LABEL,
        }));
      } else if (isTreasury) {
        const rows = documents !== null ? documents : await getTreasuryEntries();
        nextItems = (Array.isArray(rows) ? rows : []).map((row, index) => {
          const income = String(row.entry_type || '') === 'income';
          const amount = Math.abs(Number(row.amount || 0));
          const accountLabel = [row.bank_name, row.account_name].filter(Boolean).join(' · ');
          return {
            id: row.id || `mov-${index}`,
            date: row.entry_date,
            amount,
            signedAmount: income ? amount : -amount,
            flowGroup: income ? INGRESOS_LABEL : EGRESOS_LABEL,
            account: accountLabel || 'Sin cuenta',
            status: String(row.reconciliation_status || '').trim() || 'Pendiente',
            category: String(row.category || '').trim() || 'Sin categoría',
            docType: 'Movimiento',
            side: 'Total',
          };
        });
      }

      setItems(nextItems);
      if (typeof onDateValuesChange === 'function') {
        onDateValuesChange(nextItems.map((item) => parseDateSafe(item.date)).filter(Boolean));
      }
    } catch (err) {
      setError(err.message || 'No se pudo cargar la distribución.');
      setItems([]);
      if (typeof onDateValuesChange === 'function') onDateValuesChange([]);
    } finally {
      setIsLoading(false);
    }
  }, [section, isMayor, isRegister, isJournal, isVentas, isTreasury, onDateValuesChange, documents]);

  useEffect(() => {
    if (usesSharedDocuments && documents !== null) {
      if (documentsLoading) {
        setIsLoading(true);
        return;
      }
      if (documentsError) {
        setError(documentsError);
        setItems([]);
        setIsLoading(false);
        if (typeof onDateValuesChange === 'function') onDateValuesChange([]);
        return;
      }
    }
    load();
  }, [
    load,
    refreshKey,
    documents,
    documentsLoading,
    documentsError,
    usesSharedDocuments,
    onDateValuesChange,
  ]);

  useEffect(() => {
    setMonthVisibility({});
    setDimensionVisibility({});
  }, [section]);

  const dimensions = stackDimensions;
  const metrics = valueMetrics;
  const activeMetric = metrics.find((m) => m.key === metricKey) || metrics[0];
  const resolvedMetricKey = activeMetric?.key || 'amount';
  const isBreakdown = isMayor && resolvedMetricKey === 'breakdown';
  const isVentasSaldo = isVentas && resolvedMetricKey === 'saldo';
  const isFlowDetail = (isVentas || isTreasury) && resolvedMetricKey === 'detalle';
  const isMayorSaldo =
    isMayor && (resolvedMetricKey === 'saldo' || resolvedMetricKey === 'saldo_acumulado');
  const isTreasurySaldo =
    isTreasury && (resolvedMetricKey === 'saldo' || resolvedMetricKey === 'saldo_acumulado');
  const isSaldoMetric = isMayorSaldo || isVentasSaldo || isTreasurySaldo;
  const isCumulativeSaldo = (isMayor || isTreasury) && resolvedMetricKey === 'saldo_acumulado';
  const isSideBySide = isFlowDetail;

  const effectiveStackKey = useMemo(() => {
    if (isFlowDetail) return 'flowGroup';
    if (isVentasSaldo || isTreasurySaldo) return 'total';
    if (isBreakdown) return 'side';
    if (isMayorSaldo) return 'total';
    if (isMayor && (resolvedMetricKey === 'debe' || resolvedMetricKey === 'haber')) return 'side';
    return stackKey;
  }, [
    isFlowDetail,
    isVentasSaldo,
    isTreasurySaldo,
    isBreakdown,
    isMayorSaldo,
    isMayor,
    resolvedMetricKey,
    stackKey,
  ]);

  const activeDimension = useMemo(() => {
    if (isSaldoMetric) {
      return {
        ...TOTAL_DIMENSION,
        colors: { Total: SALDO_COLOR },
      };
    }
    if (isFlowDetail) return isTreasury ? TREASURY_FLOW_DIMENSION : FLOW_DIMENSION;
    if (isBreakdown || resolvedMetricKey === 'debe' || resolvedMetricKey === 'haber') {
      return SIDE_DIMENSION;
    }
    return dimensions.find((d) => d.key === effectiveStackKey) || dimensions[0] || TOTAL_DIMENSION;
  }, [
    isSaldoMetric,
    isFlowDetail,
    isTreasury,
    isBreakdown,
    resolvedMetricKey,
    dimensions,
    effectiveStackKey,
  ]);

  const filterDimensions = useMemo(() => {
    const base = [...dimensions];
    const extras = filterOnlyDimensions.filter((d) => !base.some((b) => b.key === d.key));
    return [...base, ...extras];
  }, [dimensions, filterOnlyDimensions]);

  const scopedItems = useMemo(() => {
    if (selectedPeriod) {
      return items.filter((item) => isDateInSelectedPeriod(item.date, selectedPeriod));
    }
    if (!periodRange?.start || !periodRange?.end) return items;
    return items.filter((item) => {
      const date = parseDateSafe(item.date);
      if (!date) return false;
      return date >= periodRange.start && date <= periodRange.end;
    });
  }, [items, periodRange, selectedPeriod]);

  const filteredItems = useMemo(() => {
    let next = filterItemsByDimensions(scopedItems, filterDimensions, dimensionVisibility);
    if (isMayor && resolvedMetricKey === 'debe') {
      next = next.filter((item) => item.side === 'DEBE');
    } else if (isMayor && resolvedMetricKey === 'haber') {
      next = next.filter((item) => item.side === 'HABER');
    }
    return next;
  }, [scopedItems, filterDimensions, dimensionVisibility, isMayor, resolvedMetricKey]);

  useEffect(() => {
    if (!isMayor || typeof onFocusedAccountIdsChange !== 'function') return;
    const accountVis = dimensionVisibility.account;
    const accountOptions = discoverDimensionOptions(items, ACCOUNT_DIMENSION);
    const visibleKeys = accountOptions
      .map((opt) => opt.key)
      .filter((key) => isFilterVisible(accountVis || {}, key));
    const ids = [];
    const seen = new Set();
    filteredItems.forEach((item) => {
      if (!visibleKeys.includes(item.account)) return;
      const id = item.chartAccountId;
      if (id && !seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    });
    if (!accountVis || visibleKeys.length === accountOptions.length || visibleKeys.length === 0) {
      onFocusedAccountIdsChange([]);
      return;
    }
    onFocusedAccountIdsChange(ids);
  }, [isMayor, dimensionVisibility, items, filteredItems, onFocusedAccountIdsChange]);

  const { rows, seriesPresent } = useMemo(() => {
    const discovered = new Set();
    let running = 0;

    const rowsBuilt = (periods || []).map((period) => {
      const bySeries = {};
      let periodTotal = 0;
      let periodNet = 0;
      let cargos = 0;
      let creditos = 0;

      filteredItems.forEach((item) => {
        const date = parseDateSafe(item.date);
        if (!date) return;
        if (date < period.start || date > period.end) return;

        if (isFlowDetail) {
          const amount = Number(item.amount || 0);
          if (amount <= 0) return;
          if (item.flowGroup === negativeFlowLabel) {
            creditos += amount;
            bySeries[negativeFlowLabel] = (bySeries[negativeFlowLabel] || 0) + amount;
            discovered.add(negativeFlowLabel);
          } else {
            cargos += amount;
            bySeries[positiveFlowLabel] = (bySeries[positiveFlowLabel] || 0) + amount;
            discovered.add(positiveFlowLabel);
          }
          return;
        }

        if (isSaldoMetric) {
          periodNet += Number(item.signedAmount || 0);
          return;
        }

        const series = resolveDimensionValue(
          {
            ...item,
            side: item.side,
            account: item.account,
            docType: item.docType,
            status: item.status,
            flowGroup: item.flowGroup,
            total: 'Total',
          },
          activeDimension
        );

        if (resolvedMetricKey === 'count') {
          bySeries[series] = (bySeries[series] || 0) + 1;
          periodTotal += 1;
          discovered.add(series);
          return;
        }

        const weight = Number(item.amount || 0);
        if (weight <= 0) return;
        bySeries[series] = (bySeries[series] || 0) + weight;
        periodTotal += weight;
        discovered.add(series);
      });

      if (isFlowDetail) {
        return {
          key: period.key,
          label: period.label,
          axisLabel: period.shortLabel || period.label,
          total: cargos - creditos,
          absTotal: Math.max(cargos, creditos),
          bySeries,
          cargos,
          creditos,
        };
      }

      if (isSaldoMetric) {
        if (isCumulativeSaldo) {
          running += periodNet;
          bySeries.Total = running;
          periodTotal = running;
        } else {
          bySeries.Total = periodNet;
          periodTotal = periodNet;
        }
        discovered.add('Total');
      }

      return {
        key: period.key,
        label: period.label,
        axisLabel: period.shortLabel || period.label,
        total: periodTotal,
        absTotal: Math.abs(periodTotal),
        bySeries,
      };
    });

    const preferredOrder = isFlowDetail
      ? [positiveFlowLabel, negativeFlowLabel]
      : Array.isArray(activeDimension.order)
        ? activeDimension.order
        : [];
    const seriesOrder = [
      ...preferredOrder.filter((key) => discovered.has(key)),
      ...[...discovered]
        .filter((key) => !preferredOrder.includes(key))
        .sort((a, b) => a.localeCompare(b, 'es')),
    ];

    return { rows: rowsBuilt, seriesPresent: seriesOrder };
  }, [
    periods,
    filteredItems,
    activeDimension,
    resolvedMetricKey,
    isSaldoMetric,
    isCumulativeSaldo,
    isFlowDetail,
    positiveFlowLabel,
    negativeFlowLabel,
  ]);

  const visibleRows = useMemo(
    () => rows.filter((row) => isFilterVisible(monthVisibility, row.key)),
    [rows, monthVisibility]
  );

  const seriesColors = useMemo(
    () => buildColorMap(seriesPresent, activeDimension.colors || {}, palette),
    [seriesPresent, activeDimension, palette]
  );

  const displayMetricKey = moneyMetricKey(resolvedMetricKey);
  const maxAbs = Math.max(
    1,
    ...visibleRows.map((row) =>
      isSideBySide
        ? Math.max(Number(row.cargos || 0), Number(row.creditos || 0), row.absTotal || 0)
        : row.absTotal ?? Math.abs(row.total || 0)
    )
  );
  const total = useMemo(() => {
    if (isCumulativeSaldo) {
      if (!visibleRows.length) return 0;
      return visibleRows[visibleRows.length - 1].total || 0;
    }
    return visibleRows.reduce((sum, row) => sum + Number(row.total || 0), 0);
  }, [visibleRows, isCumulativeSaldo]);

  const hasAnyData =
    visibleRows.some(
      (row) =>
        Math.abs(row.total || 0) > 0 || Number(row.cargos || 0) > 0 || Number(row.creditos || 0) > 0
    ) || filteredItems.length > 0;

  const monthOptions = useMemo(
    () => rows.map((row) => ({ key: row.key, label: row.label })),
    [rows]
  );

  const filterSections = useMemo(() => {
    const dimensionSections = filterDimensions.map((dimension) => {
      const options = discoverDimensionOptions(items, dimension).map((option) => ({
        ...option,
        color:
          option.color ||
          (dimension.key === activeDimension.key ? seriesColors[option.key] : undefined),
      }));
      return {
        key: dimension.key,
        title: dimension.label,
        options,
        visibility: dimensionVisibility[dimension.key] || {},
        onToggle: (key) =>
          setDimensionVisibility((prev) => ({
            ...prev,
            [dimension.key]: toggleVisibilityKey(prev[dimension.key] || {}, key),
          })),
        onShowAll: () =>
          setDimensionVisibility((prev) => ({
            ...prev,
            [dimension.key]: visibilityMapFromOptions(options, true),
          })),
        onHideAll: () =>
          setDimensionVisibility((prev) => ({
            ...prev,
            [dimension.key]: visibilityMapFromOptions(options, false),
          })),
      };
    });

    return [
      ...dimensionSections,
      {
        key: 'months',
        title: 'Meses',
        options: monthOptions,
        visibility: monthVisibility,
        onToggle: (key) => setMonthVisibility((prev) => toggleVisibilityKey(prev, key)),
        onShowAll: () => setMonthVisibility(visibilityMapFromOptions(monthOptions, true)),
        onHideAll: () => setMonthVisibility(visibilityMapFromOptions(monthOptions, false)),
      },
    ];
  }, [
    filterDimensions,
    items,
    dimensionVisibility,
    activeDimension.key,
    seriesColors,
    monthOptions,
    monthVisibility,
  ]);

  const title = SECTION_TITLES[section] || 'Distribución contable';
  const titleDimension = isTreasury
    ? isSaldoMetric || isFlowDetail
      ? ''
      : activeDimension.label
    : isVentasSaldo
      ? 'Saldo'
      : isFlowDetail
        ? 'Detalle'
        : activeDimension.label;
  const chartTitle = [title, titleDimension, activeMetric?.label].filter(Boolean).join(' · ');

  const border = ui.border;
  const headerBg = ui.header;
  const text = ui.text;
  const muted = ui.muted;
  const panel = ui.panel;

  const menuDimensions = useMemo(() => {
    if (isSaldoMetric || isFlowDetail) return [];
    if (isBreakdown || resolvedMetricKey === 'debe' || resolvedMetricKey === 'haber') {
      return [];
    }
    return isTreasury ? dimensions : dimensions.filter((d) => d.key !== 'flowGroup');
  }, [isSaldoMetric, isFlowDetail, isBreakdown, resolvedMetricKey, dimensions, isTreasury]);

  const monthColWidth = isSideBySide ? 56 : 44;
  const fillPlot = isTreasury || fillWidth;

  useLayoutEffect(() => {
    if (fillPlot) return undefined;
    const node = scrollRef.current;
    if (!node || isLoading) return undefined;
    const scrollToEnd = () => {
      node.scrollLeft = node.scrollWidth;
    };
    scrollToEnd();
    const frame = window.requestAnimationFrame(scrollToEnd);
    return () => window.cancelAnimationFrame(frame);
  }, [isLoading, visibleRows.length, periods, resolvedMetricKey, section, isSideBySide, fillPlot]);

  if (error) {
    return (
      <div
        style={{
          border: `1px solid ${border}`,
          borderRadius: 10,
          padding: 14,
          background: panel,
          color: ui.danger,
          fontSize: 12,
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '10px',
        overflow: 'visible',
        margin: '0',
        background: panel,
        minWidth: 0,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '7px 10px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          borderRadius: '10px 10px 0 0',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <ChartOptionsMenu
            axisModes={AXIS_MODES}
            axisKey={axisKey}
            onAxisKeyChange={() => {}}
            dimensions={menuDimensions}
            stackKey={effectiveStackKey}
            onStackKeyChange={setStackKey}
            metrics={metrics}
            metricKey={resolvedMetricKey}
            onMetricKeyChange={setMetricKey}
            filterSections={filterSections}
          />
          <h3 style={{ margin: 0, fontSize: '13px', color: text, fontWeight: 700, lineHeight: 1.2 }}>
            {isLoading ? 'Cargando distribución…' : chartTitle}
          </h3>
        </div>
        <span style={{ fontSize: '11px', color: muted, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {formatChartMetricValue(total, displayMetricKey)} {activeMetric?.totalLabel || 'CLP'}
        </span>
      </div>

      {isLoading ? (
        <div style={{ padding: '18px 12px', color: muted, fontSize: '12px', textAlign: 'center' }}>
          Cargando…
        </div>
      ) : !periods.length || !hasAnyData ? (
        <div style={{ padding: '18px 12px', color: muted, fontSize: '12px', textAlign: 'center' }}>
          Sin datos para el período seleccionado.
        </div>
      ) : (
        <>
          {seriesPresent.length > 0 ? (
            <div style={{ flexShrink: 0 }}>
              <ChartSeriesLegend series={seriesPresent} colors={seriesColors} />
            </div>
          ) : null}
          <div
            ref={scrollRef}
            style={{
              overflowX: fillPlot ? 'hidden' : 'auto',
              overflowY: 'hidden',
              padding: fillPlot ? '12px 16px 14px' : '12px 10px 8px',
              flex: 1,
              minHeight: fillPlot ? 260 : CHART_PLOT_HEIGHT_PX,
              display: 'flex',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: fillPlot ? '4px' : '8px',
                minWidth: fillPlot ? '100%' : Math.max(280, visibleRows.length * monthColWidth),
                width: fillPlot ? '100%' : Math.max(280, visibleRows.length * monthColWidth),
                height: '100%',
                minHeight: fillPlot ? 0 : `${CHART_PLOT_HEIGHT_PX}px`,
              }}
            >
              {visibleRows.map((item) => {
                if (isSideBySide) {
                  const tip = [
                    `${item.label}: ${isTreasury ? 'flujo neto' : 'saldo'} ${formatChartMetricTooltip(
                      item.total,
                      displayMetricKey
                    )}`,
                    `${positiveFlowLabel}: ${formatChartMetricTooltip(item.cargos || 0, displayMetricKey)}`,
                    `${negativeFlowLabel}: ${formatChartMetricTooltip(item.creditos || 0, displayMetricKey)}`,
                  ].join('\n');
                  return (
                    <div
                      key={item.key}
                      title={tip}
                      style={{
                        flex: fillPlot ? '1 1 0' : '0 0 auto',
                        width: fillPlot ? undefined : monthColWidth - 8,
                        minWidth: fillPlot ? 0 : monthColWidth - 8,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '6px',
                        minHeight: 0,
                      }}
                    >
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          color: text,
                          lineHeight: 1,
                          opacity:
                            Math.abs(item.total || 0) > 0 || item.cargos || item.creditos ? 1 : 0.35,
                          flexShrink: 0,
                        }}
                      >
                        {formatChartMetricValue(item.total, displayMetricKey)}
                      </span>
                      <div
                        style={{
                          flex: '1 1 0',
                          minHeight: 0,
                          width: '100%',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          gap: 3,
                        }}
                      >
                        {seriesPresent.map((series) => {
                          const value = Math.abs(Number(item.bySeries[series] || 0));
                          const heightPct = value > 0 ? (value / maxAbs) * 100 : 0;
                          return (
                            <div
                              key={series}
                              title={`${item.label} · ${series}: ${formatChartMetricTooltip(
                                item.bySeries[series] || 0,
                                displayMetricKey
                              )}`}
                              style={{
                                width: fillPlot ? '42%' : 14,
                                maxWidth: fillPlot ? 22 : 14,
                                minWidth: fillPlot ? 8 : 14,
                                height: `${heightPct}%`,
                                minHeight: value > 0 ? 3 : 0,
                                borderRadius: '5px 5px 2px 2px',
                                background:
                                  value > 0
                                    ? seriesColors[series]
                                    : `color-mix(in srgb, ${muted} 18%, ${panel})`,
                                flex: fillPlot ? '1 1 0' : '0 0 auto',
                              }}
                            />
                          );
                        })}
                      </div>
                      <span
                        style={{
                          fontSize: '9px',
                          fontWeight: 600,
                          color: muted,
                          textAlign: 'center',
                          lineHeight: 1.15,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          wordBreak: 'break-word',
                          flexShrink: 0,
                        }}
                      >
                        {item.axisLabel || item.label}
                      </span>
                    </div>
                  );
                }

                const abs = Math.abs(Number(item.total || 0));
                const heightPct = abs > 0 ? (abs / maxAbs) * 100 : 0;
                const stackTitle = [
                  `${item.label}: ${formatChartMetricTooltip(item.total, displayMetricKey)}`,
                  ...seriesPresent
                    .filter((series) => item.bySeries[series])
                    .map(
                      (series) =>
                        `${series}: ${formatChartMetricTooltip(
                          item.bySeries[series],
                          displayMetricKey
                        )}`
                    ),
                ].join('\n');

                return (
                  <div
                    key={item.key}
                    title={stackTitle}
                    style={{
                      flex: fillPlot ? '1 1 0' : '0 0 auto',
                      width: fillPlot ? undefined : monthColWidth - 8,
                      minWidth: fillPlot ? 0 : monthColWidth - 8,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      minHeight: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: text,
                        lineHeight: 1,
                        opacity: abs > 0 ? 1 : 0.35,
                        flexShrink: 0,
                      }}
                    >
                      {formatChartMetricValue(item.total, displayMetricKey)}
                    </span>
                    <div
                      style={{
                        flex: '1 1 0',
                        minHeight: 0,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'center',
                      }}
                    >
                      <div
                        style={{
                          width: '70%',
                          maxWidth: fillPlot ? 40 : 34,
                          height: `${heightPct}%`,
                          minHeight: abs > 0 ? '3px' : 0,
                          flexShrink: 0,
                          display: 'flex',
                          flexDirection: 'column-reverse',
                          overflow: 'hidden',
                          borderRadius: '5px 5px 2px 2px',
                          background:
                            abs > 0 ? 'transparent' : `color-mix(in srgb, ${muted} 18%, ${panel})`,
                        }}
                      >
                        {seriesPresent.map((series) => {
                          const count = Math.abs(Number(item.bySeries[series] || 0));
                          if (count <= 0) return null;
                          return (
                            <div
                              key={series}
                              style={{
                                flex: count,
                                minHeight: 2,
                                background: seriesColors[series],
                                width: '100%',
                              }}
                              title={`${item.label} · ${series}: ${formatChartMetricTooltip(
                                item.bySeries[series],
                                displayMetricKey
                              )}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: '9px',
                        fontWeight: 600,
                        color: muted,
                        textAlign: 'center',
                        lineHeight: 1.15,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                        flexShrink: 0,
                      }}
                    >
                      {item.axisLabel || item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountingMonthlyChart;
